<template>
  <div class="ai-tracking">
    <div class="ai-tracking-stats">
      <article class="ai-stat-card ai-stat-card--primary">
        <span>Requêtes</span>
        <strong>{{ requestStats.total }}</strong>
      </article>
      <article class="ai-stat-card">
        <span>Utilisateurs</span>
        <strong>{{ requestStats.user }}</strong>
      </article>
      <article class="ai-stat-card">
        <span>Élus</span>
        <strong>{{ requestStats.elevated_user }}</strong>
      </article>
    </div>

    <section class="ai-table-card">
      <header class="ai-table-header">
        <div>
          <h4>Premières questions</h4>
        </div>
        <div class="ai-table-header-actions">
          <p v-if="loading" class="ai-loading-copy">Chargement...</p>
          <button type="button" class="ai-primary-action" :disabled="exporting" @click="downloadCsv">
            <i class="fas fa-file-csv" aria-hidden="true"></i>
            <span>{{ exporting ? 'Export...' : 'Export CSV' }}</span>
          </button>
        </div>
      </header>

      <div class="ai-table-body">
        <b-table
          :data="firstQuestions"
          :loading="loading"
          hoverable
          default-sort="created_at"
          default-sort-direction="desc"
          :paginated="firstQuestions.length > 12"
          per-page="12"
        >
          <b-table-column field="created_at_label" label="Date" sortable searchable width="160" v-slot="props">
            {{ formatDate(props.row.created_at) }}
          </b-table-column>

          <b-table-column
            v-if="!isDomainFiltered"
            field="domain_label"
            label="Domaine"
            sortable
            searchable
            width="180"
            v-slot="props"
          >
            <span class="ai-domain-name">{{ props.row.domain_label }}</span>
          </b-table-column>

          <b-table-column field="user_search" label="Utilisateur" sortable searchable width="220" v-slot="props">
            <div class="ai-user-cell">
              <span>{{ props.row.user_label || 'Utilisateur inconnu' }}</span>
              <small v-if="props.row.user_email">{{ props.row.user_email }}</small>
            </div>
          </b-table-column>

          <b-table-column field="question" label="Question" sortable searchable v-slot="props">
            <p class="ai-question-copy">{{ props.row.question }}</p>
          </b-table-column>

          <template #empty>
            <div class="ai-empty-table">Aucune première question sur ce périmètre.</div>
          </template>
        </b-table>
      </div>
    </section>

    <section class="ai-table-card">
      <header class="ai-table-header">
        <div>
          <h4>Questions sans réponse trouvée</h4>
        </div>
      </header>

      <div class="ai-table-body">
        <b-table
          :data="unansweredQuestions"
          :loading="loading"
          hoverable
          default-sort="answer_created_at"
          default-sort-direction="desc"
          :paginated="unansweredQuestions.length > 10"
          per-page="10"
        >
          <b-table-column field="answer_created_at_label" label="Date" sortable searchable width="160" v-slot="props">
            {{ formatDate(props.row.answer_created_at) }}
          </b-table-column>

          <b-table-column
            v-if="!isDomainFiltered"
            field="domain_label"
            label="Domaine"
            sortable
            searchable
            width="180"
            v-slot="props"
          >
            <span class="ai-domain-name">{{ props.row.domain_label }}</span>
          </b-table-column>

          <b-table-column field="user_search" label="Utilisateur" sortable searchable width="220" v-slot="props">
            <div class="ai-user-cell">
              <span>{{ props.row.user_label || 'Utilisateur inconnu' }}</span>
              <small v-if="props.row.user_email">{{ props.row.user_email }}</small>
            </div>
          </b-table-column>

          <b-table-column field="question" label="Question" sortable searchable v-slot="props">
            <p class="ai-question-copy">{{ props.row.question }}</p>
          </b-table-column>

          <b-table-column field="answer" label="Réponse assistant" sortable searchable v-slot="props">
            <p class="ai-answer-copy">{{ props.row.answer }}</p>
          </b-table-column>

          <template #empty>
            <div class="ai-empty-table">Aucune question sans réponse trouvée sur ce périmètre.</div>
          </template>
        </b-table>
      </div>
    </section>
  </div>
</template>

<script>
import { format } from 'date-fns'
import aiService from '@/services/ai.service'

export default {
  name: 'app-ai-tracking',
  props: {
    aiDomain: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      loading: false,
      exporting: false,
      firstQuestions: [],
      unansweredQuestions: [],
      requestStats: {
        total: 0,
        user: 0,
        elevated_user: 0
      }
    }
  },
  computed: {
    domain () {
      return this.aiDomain || {}
    },
    domainUuid () {
      return this.domain && this.domain.uuid ? this.domain.uuid : null
    },
    isDomainFiltered () {
      return !!this.domainUuid
    }
  },
  watch: {
    aiDomain () {
      this.loadTracking()
    }
  },
  mounted () {
    this.loadTracking()
  },
  methods: {
    loadTracking () {
      this.loading = true
      aiService.listTracking(this.domainUuid).then(data => {
        this.firstQuestions = Array.isArray(data.first_questions)
          ? data.first_questions.map(this.normalizeFirstQuestion)
          : []
        this.unansweredQuestions = Array.isArray(data.unanswered_questions)
          ? data.unanswered_questions.map(this.normalizeUnansweredQuestion)
          : []
        this.requestStats = this.normalizeRequestStats(data.stats && data.stats.requests)
      }).catch(err => {
        this.notifyError(err, 'Impossible de charger le suivi Assistant IA.')
      }).finally(() => {
        this.loading = false
      })
    },
    normalizeRequestStats (stats) {
      return {
        total: Number(stats && stats.total ? stats.total : 0),
        user: Number(stats && stats.user ? stats.user : 0),
        elevated_user: Number(stats && stats.elevated_user ? stats.elevated_user : 0)
      }
    },
    normalizeFirstQuestion (row) {
      return {
        ...row,
        created_at_label: this.formatDate(row.created_at),
        user_search: this.userSearchValue(row)
      }
    },
    normalizeUnansweredQuestion (row) {
      return {
        ...row,
        answer_created_at_label: this.formatDate(row.answer_created_at),
        user_search: this.userSearchValue(row)
      }
    },
    userSearchValue (row) {
      return [row.user_label, row.user_email].filter(Boolean).join(' ')
    },
    downloadCsv () {
      this.exporting = true
      aiService.exportTrackingCsv(this.domainUuid).then(response => {
        const blob = response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `ai-questions-${format(new Date(), 'yyyy-MM-dd')}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }).catch(err => {
        this.notifyError(err, 'Impossible de générer l’export CSV.')
      }).finally(() => {
        this.exporting = false
      })
    },
    formatDate (value) {
      if (!value) {
        return ''
      }
      return format(new Date(value), 'dd/MM/yyyy HH:mm')
    },
    notifyError (err, fallback) {
      const status = err && err.response ? err.response.status : null
      this.$buefy.notification.open({
        duration: 4500,
        message: status === 403
          ? 'Vous n’avez pas le rôle requis pour consulter le suivi Assistant IA.'
          : fallback,
        position: 'is-bottom-left',
        type: 'is-danger',
        hasIcon: false
      })
    }
  }
}
</script>

<style scoped>
.ai-tracking {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ai-table-header h4 {
  margin: 0;
  color: #111827;
  font-size: 1.125rem;
  font-weight: 600;
}

.ai-table-header p {
  margin: 0.25rem 0 0;
  color: #4b5563;
  font-size: 0.875rem;
}

.ai-tracking-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.ai-stat-card {
  border: 1px solid #bfdbfe;
  border-radius: 0.75rem;
  background: #ffffff;
  padding: 0.85rem;
}

.ai-stat-card--primary {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.ai-stat-card span {
  display: block;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 700;
}

.ai-stat-card strong {
  display: block;
  margin-top: 0.35rem;
  color: #111827;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.1;
}

.ai-primary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 2.75rem;
  border: 1px solid #2563eb;
  border-radius: 0.55rem;
  background: #2563eb;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 400;
  padding: 0.65rem 0.9rem;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.ai-primary-action:hover:not(:disabled) {
  background: #1d4ed8;
  border-color: #1d4ed8;
}

.ai-primary-action:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.ai-table-card {
  overflow: hidden;
  border: 1px solid #bfdbfe;
  border-radius: 0.75rem;
  background: #ffffff;
}

.ai-table-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.ai-table-header-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}

.ai-table-body {
  overflow-x: auto;
  padding: 0.25rem 1rem 1rem;
}

.ai-domain-name {
  color: #111827;
  font-weight: 600;
}

.ai-user-cell {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.ai-user-cell span {
  color: #111827;
  font-weight: 600;
}

.ai-user-cell small {
  color: #4b5563;
  font-size: 0.75rem;
}

.ai-question-copy,
.ai-answer-copy {
  max-width: 34rem;
  margin: 0;
  color: #4b5563;
  font-size: 0.875rem;
  line-height: 1.45;
  white-space: pre-wrap;
}

.ai-answer-copy {
  max-width: 28rem;
  color: #4b5563;
}

.ai-loading-copy {
  color: #2563eb;
  font-size: 0.875rem;
  font-weight: 700;
}

.ai-empty-table {
  padding: 1.25rem;
  color: #4b5563;
  text-align: center;
}

@media (max-width: 900px) {
  .ai-table-header {
    flex-direction: column;
  }

  .ai-table-header-actions,
  .ai-primary-action {
    width: 100%;
  }

  .ai-tracking-stats {
    grid-template-columns: 1fr;
  }
}

/* Tableau Buefy : texte de corps et en-têtes de la charte. */
.ai-table-card ::v-deep .table th {
  color: #4b5563;
  font-size: 0.875rem;
  font-weight: 700;
}

.ai-table-card ::v-deep .table td {
  color: #111827;
  font-size: 0.875rem;
}
</style>
